import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { API_CONFIG } from '../config/app.config';
import logoImage from '../assets/images/app-logo.png';
import axios from 'axios';
import { useLang } from '../contexts/LangContext';

interface Channel {
  id: number;
  code: string;
  name: string;
  institution: string;
  region: string;
}

const SignupPage: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    channel: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [error, setError] = useState('');
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Local validation (email on blur, username real-time)
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/.test(formData.email);
  const emailError = emailTouched && formData.email && !emailValid ? t('auth_invalidEmail') : '';
  const usernameFormatValid = /^[a-zA-Z][a-zA-Z0-9_-]*$/.test(formData.username);
  const usernameStartsWithLetter = !formData.username || /^[a-zA-Z]/.test(formData.username);
  const usernameError = formData.username && formData.username.length < 3
    ? t('auth_usernameTooShort')
    : formData.username && !usernameStartsWithLetter
    ? t('auth_usernameMustStartWithLetter')
    : formData.username && !usernameFormatValid
    ? t('auth_usernameInvalidChars')
    : '';
  const usernameTakenError = !usernameError && !usernameChecking && usernameAvailable === false && formData.username.length >= 3
    ? t('auth_usernameTaken')
    : '';

  const validationIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" className="text-red-500"><path fill="currentColor" fillRule="evenodd" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10s-4.477 10-10 10m0-1.2a8.8 8.8 0 1 0 0-17.6a8.8 8.8 0 0 0 0 17.6m-.66-14.369h1.32l-.089 7.06H11.43l-.088-7.06zM12 17.073a.825.825 0 0 1-.835-.835a.82.82 0 0 1 .835-.835c.476 0 .835.36.835.835a.82.82 0 0 1-.835.835"/></svg>
  );
  const errorCircleIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-white"><path fill="currentColor" d="M11 14V5h2v9zm0 5v-2h2v2z"/></svg>
  );

  // Password requirements - flexible, add more checks here in the future
  const passwordMeetsRequirements = formData.password.length >= 8;
  const passwordError = passwordTouched && formData.password && !passwordMeetsRequirements ? t('auth_passwordTooShort') : '';

  const passwordsMatch = formData.password === formData.confirmPassword;
  const showMatchIndicator = formData.confirmPassword.length > 0;

  // Fetch channels on mount
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHANNELS}`);
        if (response.data.success) {
          setChannels(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch channels:', err);
        // Fallback to hardcoded channels if API fails
        setChannels([
          { id: 1, code: 'NSTU-FIMS', name: 'NSTU Faculty of Fisheries', institution: 'NSTU', region: 'Noakhali' },        
        ]);
      } finally {
        setLoadingChannels(false);
      }
    };

    fetchChannels();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    // Reset email/password validation on typing so it only shows after next blur
    if (e.target.name === 'email') setEmailTouched(false);
    if (e.target.name === 'password') setPasswordTouched(false);
  };

  // Debounced username availability check
  useEffect(() => {
    if (formData.username.length < 3) {
      setUsernameAvailable(null);
      setUsernameChecking(false);
      return;
    }

    setUsernameChecking(true);
    setUsernameAvailable(null);

    const timer = setTimeout(async () => {
      try {
        const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHECK_USERNAME}`;
        const response = await axios.post(url, {
          username: formData.username,
        });
        const available = !!response.data?.available;
        console.log('[Signup] Username check:', formData.username, 'available:', available, 'raw:', response.data);
        setUsernameAvailable(available);
      } catch (err) {
        console.error('[Signup] Username check failed:', err);
        setUsernameAvailable(null);
      } finally {
        setUsernameChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.username.length < 3) {
      setError(t('auth_usernameTooShort'));
      setLoading(false);
      return;
    }

    if (!/^[a-zA-Z]/.test(formData.username)) {
      setError(t('auth_usernameMustStartWithLetter'));
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth_passwordMismatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError(t('auth_passwordTooShort'));
      setLoading(false);
      return;
    }

    try {
      await authService.register({
        name: formData.name,
        email: formData.email,
        username: formData.username,
        password: formData.password,
        channel: formData.channel,
      });
      navigate('/');
    } catch (err: any) {
      setError(err.message || t('auth_signupError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grow bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={logoImage}
            alt={t("appTitle")}
            className="w-12 h-12 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('auth_createAccountTitle')}
          </h2>
          <p className="text-gray-600 text-sm">
            {t('auth_signupSubtitle')}
          </p>
        </div>

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label htmlFor="name" className="label block mb-2">
              {t('auth_fullName')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onFocus={handleFocus}
              required
              className="input-form"
              placeholder={t('auth_enterFullName')}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="label block mb-2">
              {t('email')}
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={() => setEmailTouched(true)}
                required
                className="input-form pr-12"
                placeholder={t('auth_enterEmail')}
              />
              {emailTouched && formData.email && !emailValid && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center"
                  >
                    {errorCircleIcon}
                  </motion.div>
                </div>
              )}
              {emailTouched && formData.email && emailValid && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-white"><path fill="currentColor" d="M18.577 6.183a1 1 0 0 1 .24 1.394l-5.666 8.02c-.36.508-.665.94-.94 1.269c-.287.34-.61.658-1.038.86a2.83 2.83 0 0 1-2.03.153c-.456-.137-.82-.406-1.149-.702c-.315-.285-.672-.668-1.09-1.116l-1.635-1.753a1 1 0 1 1 1.462-1.364l1.606 1.722c.455.487.754.806.998 1.027c.24.216.344.259.385.271c.196.060.405.045.598-.046c.046-.022.149-.085.36-.338c.216-.257.473-.62.863-1.171l5.642-7.986a1 1 0 0 1 1.394-.24"/></svg>
                  </motion.div>
                </div>
              )}
            </div>
            {emailError ? (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                {validationIcon}
                {emailError}
              </p>
            ) : null}
          </div>

          {/* Username */}
          <div>
            <label htmlFor="username" className="label block mb-2">
              {t('username')}
            </label>
            <div className="relative">
              <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              onFocus={handleFocus}
              required
              minLength={3}
              pattern="[a-zA-Z][a-zA-Z0-9_-]*"
              className="input-form pr-12"
              placeholder={t('auth_chooseUsername')}
            />
            <div className="absolute inset-y-0 right-3 flex items-center gap-1">
              {formData.username && !usernameFormatValid && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center"
                >
                  {errorCircleIcon}
                </motion.div>
              )}
              {usernameChecking && !(formData.username && !usernameFormatValid) && (
                <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              )}
              {!usernameChecking && !(formData.username && !usernameFormatValid) && usernameAvailable === null && formData.username.length >= 3 && (
                <div className="w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center" title="Could not verify username">
                  <span className="text-white text-sm font-bold">!</span>
                </div>
              )}
              {!usernameChecking && !(formData.username && !usernameFormatValid) && usernameAvailable === true && formData.username.length >= 3 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-white"><path fill="currentColor" d="M18.577 6.183a1 1 0 0 1 .24 1.394l-5.666 8.02c-.36.508-.665.94-.94 1.269c-.287.34-.61.658-1.038.86a2.83 2.83 0 0 1-2.03.153c-.456-.137-.82-.406-1.149-.702c-.315-.285-.672-.668-1.09-1.116l-1.635-1.753a1 1 0 1 1 1.462-1.364l1.606 1.722c.455.487.754.806.998 1.027c.24.216.344.259.385.271c.196.060.405.045.598-.046c.046-.022.149-.085.36-.338c.216-.257.473-.62.863-1.171l5.642-7.986a1 1 0 0 1 1.394-.24"/></svg>
                </motion.div>
              )}
              {!usernameChecking && !(formData.username && !usernameFormatValid) && usernameAvailable === false && formData.username.length >= 3 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-white"><g fill="currentColor" fillRule="evenodd" clipRule="evenodd"><path d="M5.47 5.47a.75.75 0 0 1 1.06 0l12 12a.75.75 0 1 1-1.06 1.06l-12-12a.75.75 0 0 1 0-1.06"/><path d="M18.53 5.47a.75.75 0 0 1 0 1.06l-12 12a.75.75 0 0 1-1.06-1.06l12-12a.75.75 0 0 1 1.06 0"/></g></svg>
                </motion.div>
              )}
            </div>
            </div>
            {usernameError ? (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                {validationIcon}
                {usernameError}
              </p>
            ) : usernameTakenError ? (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                {validationIcon}
                {usernameTakenError}
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                {t('auth_usernameHint')}
              </p>
            )}
          </div>

          {/* Channel */}
          <div>
            <label htmlFor="channel" className="label block mb-2">
              {t('auth_channel')}
            </label>
            <select
              id="channel"
              name="channel"
              value={formData.channel}
              onChange={handleChange}
              required
              disabled={loadingChannels}
              className="select-form disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">
                {loadingChannels 
                  ? t('auth_loadingChannels') 
                  : t('auth_selectChannel')}
              </option>
              {channels.map((channel) => (
                <option key={channel.id} value={channel.code}>
                  {channel.code} ({channel.name})
                </option>
              ))}
            </select>
            {!loadingChannels && channels.length === 0 && (
              <p className="text-xs text-red-500 ml-2 mt-1">
                {t('auth_noChannelsAvailable')}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="label block mb-2">
              {t('password')}
            </label>
            <div className="relative">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={() => setPasswordTouched(true)}
                required
                minLength={8}
                className="input-form pr-12"
                placeholder={t('auth_enterPassword')}
              />
              {passwordTouched && formData.password && !passwordMeetsRequirements && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center"
                  >
                    {errorCircleIcon}
                  </motion.div>
                </div>
              )}
              {passwordTouched && formData.password && passwordMeetsRequirements && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-white"><path fill="currentColor" d="M18.577 6.183a1 1 0 0 1 .24 1.394l-5.666 8.02c-.36.508-.665.94-.94 1.269c-.287.34-.61.658-1.038.86a2.83 2.83 0 0 1-2.03.153c-.456-.137-.82-.406-1.149-.702c-.315-.285-.672-.668-1.09-1.116l-1.635-1.753a1 1 0 1 1 1.462-1.364l1.606 1.722c.455.487.754.806.998 1.027c.24.216.344.259.385.271c.196.060.405.045.598-.046c.046-.022.149-.085.36-.338c.216-.257.473-.62.863-1.171l5.642-7.986a1 1 0 0 1 1.394-.24"/></svg>
                  </motion.div>
                </div>
              )}
            </div>
            {passwordError ? (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                {validationIcon}
                {passwordError}
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                {t('auth_passwordHint')}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="label block mb-2">
              {t('auth_confirmPassword')}
            </label>
            <div className="relative">
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onFocus={handleFocus}
                required
                minLength={8}
                className="input-form pr-12"
                placeholder={t('auth_confirmPasswordPlaceholder')}
              />
              {showMatchIndicator && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    key={passwordsMatch ? 'match' : 'mismatch'}
                    className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      passwordsMatch ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  >
                  {passwordsMatch ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-white"><path fill="currentColor" d="M18.577 6.183a1 1 0 0 1 .24 1.394l-5.666 8.02c-.36.508-.665.94-.94 1.269c-.287.34-.61.658-1.038.86a2.83 2.83 0 0 1-2.03.153c-.456-.137-.82-.406-1.149-.702c-.315-.285-.672-.668-1.09-1.116l-1.635-1.753a1 1 0 1 1 1.462-1.364l1.606 1.722c.455.487.754.806.998 1.027c.24.216.344.259.385.271c.196.06.405.045.598-.046c.046-.022.149-.085.36-.338c.216-.257.473-.62.863-1.171l5.642-7.986a1 1 0 0 1 1.394-.24"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-white"><g fill="currentColor" fillRule="evenodd" clipRule="evenodd"><path d="M5.47 5.47a.75.75 0 0 1 1.06 0l12 12a.75.75 0 1 1-1.06 1.06l-12-12a.75.75 0 0 1 0-1.06"/><path d="M18.53 5.47a.75.75 0 0 1 0 1.06l-12 12a.75.75 0 0 1-1.06-1.06l12-12a.75.75 0 0 1 1.06 0"/></g></svg>
                  )}
                </motion.div>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
            >
              {error}
            </motion.div>
          )}

          {/* Signup Button */}
          <button
            type="submit"
            disabled={loading}
            className="btn-form btn-form-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? t('auth_creatingAccount') : t('auth_createAccountTitle')}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            {t('auth_alreadyHaveAccount')}{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-blue-600 font-semibold hover:underline"
            >
              {t('auth_login')}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SignupPage;
